import apiUtil, { clientHeaders, CLIENT_HEADER } from '@/utils/api'
import { domainBuilder } from '@/utils/domainBuilder'
import {
	User,
	RequestResetPassword,
	ResetPasswordPayload,
	RequestGeneratePassword,
	RequestMFA
} from '@/models'

export type ConsentStatus =
	| 'CONSENT_REQUESTED'
	| 'CONSENT_GRANTED'
	| 'CONSENT_REVOKED'
	| 'CONSENT_REMINDED'
	| 'CONSENT_OVERRIDED'
	| 'CONSENT_NOT_REQUESTED'
	| 'CONSENT_LINK_INVALIDATED'
	| 'CONSENT_PARAMETER_INVALID'
	| 'CONSENT_REJECTED'

export interface ConsentDetailsApiResponse {
	consent_code: string
	service: string
	requester_name: string
	consenter_name: string | null
	consenter_npwp: string | null
	consent_status: string
	npwp?: string
	npwp16?: string
	subscription_id?: string | null
	created_at?: number
	updated_at?: number
	consent_email?: string
	consent_phone?: string
	consent_expiry_date?: string | null
	is_third_party_consent?: boolean
	attempt_count?: number
	subject_name?: string
}

export interface ConsentSubmitResponse {
	status?: string
	code?: number | string
	data?: string
	message?: string
}

export type ConsentSubmitPayload =
	| { status: 'APPROVED'; signatoryNpwp: string; authKey: string }
	| { status: 'DECLINED'; signatoryNpwp: string; authKey: string }

const authApiUrl = `${domainBuilder()}/auth/api`

export default {
	getCurrentUser() {
		return apiUtil.get(`${authApiUrl}/user`)
	},
	async signInAction(payload: User) {
		return await apiUtil.post(`${authApiUrl}/login`, payload)
	},
	async getMfaDetailsAction() {
		return await apiUtil.get(`${authApiUrl}/mfa`)
	},
	async validateMfaAction(payload: RequestMFA) {
		return await apiUtil.post(`${authApiUrl}/mfa`, payload)
	},
	async resendMfaAction() {
		return await apiUtil.post(`${authApiUrl}/mfa`, { resend: 'true' })
	},
	async setupMFAAction(payload: { phone: string }) {
		return await apiUtil.post(`${authApiUrl}/mfa/setup`, payload)
	},
	async activateMFAAction(payload: { mfaId: string; mfa: string }) {
		return await apiUtil.post(`${authApiUrl}/mfa/activate`, payload)
	},
	async signUpAction(payload: User) {
		return await apiUtil.post(`${authApiUrl}/register`, payload)
	},
	async resetPasswordAction(payload: RequestResetPassword) {
		const { message } = await apiUtil.post(`${authApiUrl}/reset_password`, payload)
		return message
	},
	async checkTokenRequest(token: string) {
		try {
			const { valid } = await apiUtil.get(`${authApiUrl}/reset_password/${token}`)
			return valid
		} catch (err) {
			return err.response.data.valid || false
		}
	},
	async resetPasswordByTokenAction(payload: ResetPasswordPayload) {
		return await apiUtil.post(`${authApiUrl}/reset_password_by_token`, payload)
	},
	async getGuestUserDetails(userId: string) {
		try {
			const { data } = await apiUtil.get(`${authApiUrl}/get_guest_details/${userId}`)
			return data.email
		} catch (err) {
			return ''
		}
	},
	async savePasswordAndLogin(payload: { userId: string } & RequestGeneratePassword) {
		try {
			const { data } = await apiUtil.post(
				`${authApiUrl}/generate_password_guest_user`,
				payload
			)
			return data.redirectUrl
		} catch (err) {
			console.error(err)
			return null
		}
	},

	async getConsentGrantDetails(consentCode: string): Promise<ConsentDetailsApiResponse> {
		return (await apiUtil.get(
			`${authApiUrl}/consents/${consentCode}`
		)) as ConsentDetailsApiResponse
	},

	async consentSubmit(
		consentCode: string,
		payload: ConsentSubmitPayload
	): Promise<ConsentSubmitResponse> {
		return (await apiUtil.post(
			`${authApiUrl}/consents/${consentCode}`,
			payload,
			clientHeaders()
				.with(CLIENT_HEADER.USER_AGENT)
				.with(CLIENT_HEADER.TIMEZONE)
				.with(CLIENT_HEADER.LANGUAGE)
				.build()
		)) as ConsentSubmitResponse
	}
}
